import React, { useState, useEffect, FC } from "react";
import styled, { keyframes, css } from "styled-components";
import { Sparkles, X } from "lucide-react";
import { Link } from "react-router-dom";
import useNavigateToFibiVoice from "../../../../../hooks/useNavigateToFibiVoice";
import { mediaObj } from "../../../../../styles/Media";

interface Props {
  isOver?: boolean;
}

const EnhancedBanner: FC<Props> = ({ isOver }) => {
  const { url } = useNavigateToFibiVoice();
  const [isVisible, setIsVisible] = useState(true);
  const [isAnimated, setIsAnimated] = useState(false);

  useEffect(() => {
    // Trigger animation after component mounts
    setTimeout(() => setIsAnimated(true), 100);
  }, []);

  if (!isVisible) return null;

  return (
    <BannerContainer $isAnimated={isAnimated} $isOver={isOver}>
      {/* Floating particles effect */}
      <ParticlesContainer>
        {[...Array(6)].map((_, i) => (
          <Particle key={i} $index={i} />
        ))}
      </ParticlesContainer>

      <ContentContainer>
        {/* Animated sparkle icon */}
        <IconContainer>
          <SparkleIcon />
          <SparkleIconPing />
        </IconContainer>

        {/* Main text */}
        <BannerText>
          {isOver
            ? "Keep Your FiBi AI Advantage – Don’t Lose Access!"
            : "Get Your Free FiBi AI Trial – For a Limited Time!"}
        </BannerText>

        {/* Enhanced CTA button */}
        <CTAButton $isAnimated={isAnimated}>
          <ButtonText to={url}>
            {isOver ? "Subscribe Now" : "Start Free Trial Now"}
          </ButtonText>
          <ButtonShine />
          <ButtonGlow />
        </CTAButton>
      </ContentContainer>

      {/* Close button */}
      <CloseButton
        onClick={() => setIsVisible(false)}
        aria-label="Close banner"
      >
        <CloseIcon />
      </CloseButton>

      {/* Progress bar */}
      <ProgressContainer>
        <ProgressBar />
      </ProgressContainer>
    </BannerContainer>
  );
};

export default EnhancedBanner;

// Keyframe animations
const gradientShift = keyframes`
  0%, 100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
`;

const shimmer = keyframes`
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px) scale(1);
    opacity: 0.5;
  }
  50% {
    transform: translateY(-20px) scale(1.1);
    opacity: 1;
  }
`;

const progress = keyframes`
  0% {
    width: 0%;
    opacity: 1;
  }
  90% {
    width: 100%;
    opacity: 1;
  }
  100% {
    width: 100%;
    opacity: 0;
  }
`;

const pulseGentle = keyframes`
  0%, 100% {
    box-shadow: 0 4px 15px rgba(255, 255, 255, 0.2);
  }
  50% {
    box-shadow: 0 6px 20px rgba(255, 255, 255, 0.4);
  }
`;

// Styled components
const BannerContainer = styled.div<{ $isAnimated: boolean; $isOver?: boolean }>`
  position: relative;
  overflow: hidden;
  background: linear-gradient(90deg, #0076e9, #0066cc, #0076e9);
  background-size: 200% 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  padding: 12px 16px;
  animation: ${gradientShift} 4s ease-in-out infinite;
  transform: ${(props) =>
    props.$isAnimated ? "translateY(0)" : "translateY(-100%)"};
  opacity: ${(props) => (props.$isAnimated ? 1 : 0)};
  transition: all 0.7s ease-out;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.1),
      transparent
    );
    transform: translateX(-100%);
    animation: ${shimmer} 3s ease-in-out infinite;
  }

  ${mediaObj.tablet} {
    padding: ${(props) =>
      props.$isOver ? "12px 16px 12px 60px" : "12px 16px"};
  }
`;

const ParticlesContainer = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
`;

const Particle = styled.div<{ $index: number }>`
  position: absolute;
  width: 4px;
  height: 4px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  left: ${(props) => 20 + props.$index * 15}%;
  animation: ${float} ${(props) => 3 + props.$index * 0.5}s ease-in-out infinite;
  animation-delay: ${(props) => props.$index * 0.5}s;
`;

const ContentContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  position: relative;
  z-index: 10;

  ${mediaObj.mobile} {
    flex-direction: column;
    justify-content: center;
    text-align: center;
  }
`;

const IconContainer = styled.div`
  position: relative;
`;

const SparkleIcon = styled(Sparkles)`
  width: 20px;
  height: 20px;
  color: #fde047;
  animation: pulse 2s infinite;
`;

const SparkleIconPing = styled(Sparkles)`
  position: absolute;
  inset: 0;
  width: 20px;
  height: 20px;
  color: #fef3c7;
  animation: ping 10s infinite;
  opacity: 0.75;
`;

const BannerText = styled.span`
  font-weight: 500;
  font-size: 14px;

  @media (min-width: 640px) {
    font-size: 16px;
  }
`;

const CTAButton = styled.button<{ $isAnimated: boolean }>`
  position: relative;
  padding: 8px 16px;
  background: white;
  color: #0076e9;
  border-radius: 9999px;
  font-weight: 600;
  font-size: 14px;
  border: none;
  cursor: pointer;
  transform: scale(1);
  transition: all 0.3s ease-out;
  box-shadow: 0 4px 15px rgba(255, 255, 255, 0.2);
  animation: ${(props) =>
    props.$isAnimated
      ? css`
          ${pulseGentle} 2s ease-in-out infinite
        `
      : "none"};

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 6px 20px rgba(255, 255, 255, 0.3);
    background: #f0f9ff;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.5);
  }
`;

const ButtonText = styled(Link)`
  position: relative;
  z-index: 10;
`;

const ButtonShine = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 9999px;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.2),
    transparent
  );
  transform: translateX(-100%);
  transition: transform 0.7s ease-out;

  ${CTAButton}:hover & {
    transform: translateX(100%);
  }
`;

const ButtonGlow = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.2);
  opacity: 0;
  transition: opacity 0.3s;
  filter: blur(4px);

  ${CTAButton}:hover & {
    opacity: 1;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  padding: 4px;
  border-radius: 50%;
  background: transparent;
  border: none;
  color: white;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const CloseIcon = styled(X)`
  width: 16px;
  height: 16px;
  transition: transform 0.2s;

  ${CloseButton}:hover & {
    transform: rotate(90deg);
  }
`;

const ProgressContainer = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 4px;
  background: rgba(255, 255, 255, 0.2);
`;

const ProgressBar = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #e3bdf7, #a881db);
  animation: ${progress} 8s linear infinite;
`;
